import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { formatPriceWithNoUnit } from '@/utils/formatPrice';
import { BookingFormSection } from '@/components/BookingFormCustom';

interface ServiceItem {
  id: string;
  name: string;
  price: number;
}

interface AdditionalServicesSectionProps {
  services: ServiceItem[];
  selectedServiceIds: string[];
  onSelectService: (isSelected: boolean, serviceId: string) => void;
}

const AdditionalServicesSection: React.FC<AdditionalServicesSectionProps> = ({
  services,
  selectedServiceIds,
  onSelectService,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(['validate', 'common']);

  return (
    <BookingFormSection sx={{ paddingTop: { xs: '0', md: '0' } }}>
      <Typography variant='h5' fontWeight={400} fontSize={{ xs: 12, md: 14 }} mb={1}>{t('common:additionalService')}</Typography>
      {!!services && services.length > 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            flexWrap: { xs: 'nowrap', md: 'wrap' },
            gap: 1.5,
            width: '100%',
            maxHeight: { xs: services.length > 3 ? 280 : 'auto', md: 'auto' },
            overflowY: { xs: services.length > 3 ? 'auto' : 'visible', md: 'visible' },
            overflowX: { xs: 'hidden', md: services.length > 6 ? 'auto' : 'visible' },
            ...(services.length > 6 && {
              flexWrap: { md: 'nowrap' },
            }),
          }}
        >
          {services.map((s, index) => {
            const isSelected = selectedServiceIds.includes(s.id);
            return (
              <Box
                key={`${s.id}-${index}`}
                onClick={() => onSelectService(!isSelected, s.id)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  padding: '6px',
                  borderRadius: "6px",
                  border: `1px solid ${isSelected ? theme.palette.yellow.deepPeach : theme.palette.black.silver}`,
                  backgroundColor: isSelected ? theme.palette.yellow.papayaWhip : theme.palette.white.main,
                  cursor: 'pointer',
                  minWidth: { xs: '100%', md: 180 },
                  width: { xs: '100%', md: 'calc(33.33% - 12px)' },
                  flexShrink: 0,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: `${isSelected ? theme.palette.yellow.deepPeach : theme.palette.black.silver}CC`,
                    backgroundColor: `${isSelected ? theme.palette.yellow.papayaWhip : theme.palette.white.main}CC`,
                  },
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight={500}
                  sx={{ color: isSelected ? theme.palette.other.burntOrange : theme.palette.black.jetBlack, mb: 0.5 }}
                >
                  {s.name}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={500}
                  fontSize={14}
                  sx={{ color: isSelected ? theme.palette.other.burntOrange : theme.palette.black.jetBlack }}
                >
                  {formatPriceWithNoUnit(s.price)} vnđ
                </Typography>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: 80 }}>
          <Typography variant='body1' fontWeight={400} textAlign="center" color="text.secondary">
            {t('common:noData')}
          </Typography>
        </Box>
      )}
    </BookingFormSection>
  );
};

export default AdditionalServicesSection;
